import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy, useEffect, useState } from "react";

import NotFound from "./pages/NotFound";
import TradeQuoteResponsePage from "./pages/TradeQuoteResponsePage";
import TradesPurpleRoutes from "./Routes";
import OfflineNotice from "./components/shared/OfflineNotice";
import { useKeyboardAdjust } from "./hooks/useKeyboardAdjust";

// Lazy load the offline fallback component to reduce initial bundle size
const OfflineFallback = lazy(() => import("./components/shared/OfflineFallback"));

const queryClient = new QueryClient();

const App = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Apply keyboard adjustments
  useKeyboardAdjust();

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              {isOffline && (
                <Suspense fallback={null}>
                  <OfflineFallback showHomeLink={false} />
                </Suspense>
              )}

              {!isOffline && (
                <Routes>
                  {/* TradesPurple routes - auth handling is inside TradesPurpleRoutes */}
                  <Route path="/*" element={<TradesPurpleRoutes />} />

                  {/* Public quote response route (no auth required) */}
                  <Route path="/trades/quote/:token" element={<TradeQuoteResponsePage />} />

                  {/* 404 route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              )}

              {/* Offline notice - will only show when needed */}
              <OfflineNotice />
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
